"use client";

import Image from "next/image";
import Link from "next/link";
import type { ReactNode } from "react";

/**
 * Colour overrides for the translucent-over-hero variant (the landing page
 * fades its nav from white-on-photo to navy-on-white as the hero scrolls
 * away). Solid pages leave this unset and get the standard treatment.
 */
export type SiteHeaderTone = {
  background?: string;
  borderColor?: string;
  boxShadow?: string;
  /** Wordmark colour. */
  text?: string;
  /** Tagline colour. */
  muted?: string;
};

/**
 * Standard header action pill. Shared so every page's header button (Home,
 * Sign in, Sign out, My account, Back to pricing) looks identical.
 */
export const HEADER_PILL =
  "inline-flex shrink-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-full border border-[#D5DFEF] bg-white px-4 py-2.5 text-sm font-semibold text-[#3D69B0] transition hover:border-[#3D69B0] hover:bg-[#F5F8FC]";

type SiteHeaderProps = {
  /**
   * Small uppercase line under the wordmark. This is the per-page
   * distinction: "In Partnership with Fold" on home, "Account" on the
   * account page, "Dry cleaning prices" on the price list, and so on.
   */
  tagline?: ReactNode;
  /** Right-hand slot: nav links, buttons, or a back link. */
  actions?: ReactNode;
  /** Where the wordmark links. Ignored when onBrandClick is given. */
  href?: string;
  /** Renders the wordmark as a button instead of a link (home scroll-to-top). */
  onBrandClick?: () => void;
  tone?: SiteHeaderTone;
  /** fixed sits over a hero image; sticky is the default for content pages. */
  position?: "sticky" | "fixed";
  /** Container width, matched to the page's own content column. */
  width?: "6xl" | "3xl";
  /** Hide the tagline on narrow screens when the actions slot is crowded. */
  hideTaglineOnMobile?: boolean;
};

/**
 * The single site header. Every customer-facing page renders this so the
 * chrome is identical across the site; pages differ only in their tagline and
 * their right-hand actions. (The admin console and runner app keep their own
 * purpose-built shells.)
 */
export function SiteHeader({
  tagline,
  actions,
  href = "/",
  onBrandClick,
  tone,
  position = "sticky",
  width = "6xl",
  hideTaglineOnMobile = false,
}: SiteHeaderProps) {
  const brand = (
    <>
      <Image
        src="/assets/logo.svg"
        alt="B-Laundry logo"
        width={40}
        height={40}
        priority
        className="h-[35px] w-auto sm:h-[40px]"
      />
      <div className="flex flex-col">
        <span
          translate="no"
          className="notranslate text-xl font-semibold text-[#3D69B0] sm:text-2xl"
          style={tone?.text ? { color: tone.text } : undefined}
        >
          B-Laundry
        </span>
        {tagline ? (
          <span
            className={`${
              hideTaglineOnMobile ? "hidden sm:block" : ""
            } text-[10px] font-medium uppercase tracking-[0.18em] text-[#5E6A80] sm:text-xs`}
            style={tone?.muted ? { color: tone.muted } : undefined}
          >
            {tagline}
          </span>
        ) : null}
      </div>
    </>
  );

  const brandClass = "flex min-w-0 items-center gap-2 text-left leading-tight sm:gap-3";

  return (
    <header
      className={`${
        position === "fixed" ? "fixed" : "sticky"
      } top-0 z-50 w-full border-b backdrop-blur ${
        tone ? "" : "border-[#E7EDF5] bg-white/80"
      }`}
      style={
        tone
          ? {
              backgroundColor: tone.background,
              borderColor: tone.borderColor,
              boxShadow: tone.boxShadow,
            }
          : undefined
      }
    >
      <div
        className={`mx-auto flex w-full ${
          width === "3xl" ? "max-w-3xl" : "max-w-6xl"
        } flex-wrap items-center justify-between gap-3 px-4 py-4 sm:px-6`}
      >
        {onBrandClick ? (
          <button type="button" onClick={onBrandClick} className={brandClass}>
            {brand}
          </button>
        ) : (
          <Link href={href} className={brandClass}>
            {brand}
          </Link>
        )}
        {actions ? (
          <div className="flex flex-wrap items-center gap-2 md:gap-4">{actions}</div>
        ) : null}
      </div>
    </header>
  );
}
